import {
    Box,
    Button,
    CircularProgress,
    Typography,
    Select,
    MenuItem,
    SelectChangeEvent,
    TextField,
    FormHelperText,
} from '@mui/material'
import MapContainer from './MapContainer'
import RadioInput, { RadioListType } from './RadioInput'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import RangeInput from './RangeInput'
import MapContainerPolygon from './MapContainerPolygon'
import AutoSuggestPlaces from './MapAutoComplete'
import { fetchStateList } from '../../services/entityServices'
import TruncatedText from './TruncatedText'
import { useTranslation } from 'react-i18next'
import {
    defaultGeoFenceRadius,
    locationChangeType,
    MapTypes,
} from '../../constants/constants'
import { isValidLatitude, isValidLongitude } from '../../utils/MapUtils'

interface MapSpacePropsType {
    onAddLocation: (value: GeoJSON.FeatureCollection) => void
    value: GeoJSON.FeatureCollection
    disableEdit: boolean
}

interface IState {
    name: string
    stateCode: string
}

const MapSpace = (props: MapSpacePropsType): ReactElement => {
    const { t } = useTranslation()
    const { onAddLocation, value, disableEdit } = props
    const [location, setLocation] = useState('')
    const [selectedMapType, setSelectedMapType] = useState<
        typeof MapTypes.circle | typeof MapTypes.polygon
    >(MapTypes.circle)
    const [range, setRange] = useState<number>(defaultGeoFenceRadius)
    const [points, setPoints] = useState<number[][]>([])
    const [selectedPosition, setSelectedPosition] = useState<{
        lat?: number
        lng?: number
    } | null>(null)
    const [zoom, setZoom] = useState<number>(15)
    const [country_iso_code, setCountryIsoCode] = useState('')
    const [state_iso_code, setStateIsoCode] = useState('')
    const [isGeoCodingLoading, setIsGeoCodingLoading] = useState(false)
    const [stateList, setStateList] = useState<IState[]>([])
    const [isLoadingStateList, setIsLoadingStateList] = useState(false)
    const [ovverideStateCode, setOverrideStateCode] = useState(false)
    const [google_map_state_iso_code, setGoogleMapStateIsoCode] = useState('')
    const [latError, setLatError] = useState('')
    const [lngError, setLngError] = useState('')
    const [coordsGeocodedFlag, setCoordsGeocodedFlag] = useState(false)

    useEffect(() => {
        if (value) {
            const properties = value.features?.find(
                (t) =>
                    t.geometry.type === 'Point' ||
                    t.geometry.type === MapTypes.polygon,
            )?.properties
            const address = properties?.address
            const country_iso_code: string = properties?.country_iso_code
            const state_iso_code: string = properties?.state_iso_code
            const google_map_state_iso_code: string =
                properties?.google_map_state_iso_code
            const stateISOCodeOverriden: boolean =
                !!google_map_state_iso_code &&
                !!state_iso_code &&
                state_iso_code !== google_map_state_iso_code

            const fetchStates = async () => {
                if (country_iso_code) {
                    setIsLoadingStateList(true)
                    const states: IState[] =
                        await fetchStateList(country_iso_code)
                    setIsLoadingStateList(false)
                    setStateList(states)
                }
            }
            fetchStates()

            const pointFeature = value.features?.find(
                (t) => t.geometry.type === 'Point',
            )
            const markPoint =
                pointFeature?.geometry.type === 'Point'
                    ? pointFeature.geometry.coordinates
                    : null

            let updatselectedPosition = null
            if (markPoint) {
                updatselectedPosition = {
                    lat: markPoint[1],
                    lng: markPoint[0],
                }
            } else {
                const markPolyPoint = value.features?.find(
                    (t) => t.geometry.type === MapTypes.polygon,
                )?.properties?.coordinates
                if (markPolyPoint) {
                    updatselectedPosition = {
                        lat: markPolyPoint?.[1],
                        lng: markPolyPoint?.[0],
                    }
                }
            }

            const radius = properties?.radius

            const settedZoom = properties?.zoom

            const mapType = properties?.mapType

            setLocation(address)
            setOverrideStateCode(stateISOCodeOverriden)
            setCountryIsoCode(country_iso_code)
            setStateIsoCode(state_iso_code)
            setGoogleMapStateIsoCode(google_map_state_iso_code)
            setSelectedPosition(updatselectedPosition)
            setCoordsGeocodedFlag(true)

            setRange(radius)
            setSelectedMapType(mapType || MapTypes.polygon)

            setTimeout(() => {
                setZoom(settedZoom || 15)
            }, 1000)

            const polygonFeature = value.features?.find(
                (t) => t.geometry.type === MapTypes.polygon,
            )
            const polygon =
                polygonFeature?.geometry.type === MapTypes.polygon
                    ? polygonFeature.geometry.coordinates
                    : null
            if (polygon?.length) {
                setPoints(polygon[0]?.slice(0, polygon[0]?.length - 1))
            }
        }
    }, [value])

    const maptypeList: RadioListType[] = [
        {
            label: t(MapTypes.circle),
            value: MapTypes.circle,
        },
        {
            label: t(MapTypes.polygon),
            value: MapTypes.polygon,
        },
    ]

    const geoCode = useCallback((request: google.maps.GeocoderRequest) => {
        const geocoder = new google.maps.Geocoder()
        setIsGeoCodingLoading(true)
        geocoder.geocode(request, async (results, status) => {
            setIsGeoCodingLoading(false)
            if (status === google.maps.GeocoderStatus.OK && results?.length) {
                const addressComponents = results[0].address_components
                const newMarkerPosition = {
                    lat: results[0].geometry.location.lat(),
                    lng: results[0].geometry.location.lng(),
                }
                const country = addressComponents.find((component) =>
                    component.types.includes('country'),
                )
                const state = addressComponents.find((component) =>
                    component.types.includes('administrative_area_level_1'),
                )
                if (country) {
                    const country_iso_code = country.short_name
                    setCountryIsoCode(country_iso_code)
                    setIsLoadingStateList(true)
                    const stateList: IState[] =
                        await fetchStateList(country_iso_code)
                    setIsLoadingStateList(false)
                    setStateList(stateList)

                    if (state) {
                        const google_state_code = state.short_name
                        setGoogleMapStateIsoCode(google_state_code)

                        // Check if Google's state code exists in state list
                        const stateExists = stateList.some(
                            (s) => s.stateCode === google_state_code,
                        )
                        if (stateExists) {
                            setStateIsoCode(google_state_code)
                            setOverrideStateCode(false)
                        } else {
                            const state = stateList.find(
                                (s) => s.name === google_state_code, //Some times google_state_code matches with name instead of code
                            )
                            setStateIsoCode(state?.stateCode ?? '')
                            setOverrideStateCode(true)
                        }
                    } else {
                        setStateIsoCode('')
                        setGoogleMapStateIsoCode('')
                        setOverrideStateCode(true)
                    }
                } else {
                    setCountryIsoCode('')
                    setStateIsoCode('')
                    setGoogleMapStateIsoCode('')
                    setStateList([])
                    setOverrideStateCode(false)
                }
                setSelectedPosition(newMarkerPosition)
                setLatError('')
                setLngError('')
                setCoordsGeocodedFlag(true)
            } else {
                console.error('Geocoding failed:', status)
                // Reset position when geocoding fails
                setSelectedPosition(null)
                setLatError('')
                setLngError('')
            }
        })
    }, [])

    const handleSearchInputChange = useCallback(
        (searchValue: string) => {
            if (searchValue.trim()) {
                geoCode({ address: searchValue })
            } else {
                setSelectedPosition(null) // Clear marker if search is empty
                setCountryIsoCode('')
                setStateIsoCode('')
                setStateList([])
            }
        },
        [geoCode],
    )

    // console.log('Selected Position:', selectedPosition)
    const onSearchLocation = useCallback(
        (value: string, type: string) => {
            setLocation(value)
            if (value !== location) {
                setCoordsGeocodedFlag(false)
                setSelectedPosition(null)
                setCountryIsoCode('')
                setStateIsoCode('')
                setStateList([])
                setLatError('')
                setLngError('')
            }
            if (type === locationChangeType.OPTION_SELECTED) {
                handleSearchInputChange(value)
            }
        },
        [handleSearchInputChange, location],
    )

    const onSelectMapType = useCallback((id: string, value: string) => {
        setSelectedMapType(
            value as typeof MapTypes.circle | typeof MapTypes.polygon,
        )
    }, [])

    const onChangeRange = useCallback((value: number) => {
        setRange(value)
    }, [])

    const AddLocationDetails = () => {
        const geoJSONSample: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: [],
        }
        if (!location || !selectedPosition?.lat || !selectedPosition?.lng) {
            return
        }
        if (selectedMapType === MapTypes.circle && selectedPosition) {
            const updatedFeature: GeoJSON.Feature = {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [selectedPosition?.lng, selectedPosition?.lat],
                },
                properties: {
                    address: location,
                    coordinates: [selectedPosition?.lng, selectedPosition?.lat],
                    radius: range,
                    mapType: selectedMapType,
                    country_iso_code,
                    state_iso_code,
                    google_map_state_iso_code,
                    zoom,
                },
            }
            geoJSONSample.features = [...geoJSONSample.features, updatedFeature]
        } else if (selectedMapType === MapTypes.polygon && selectedPosition) {
            const updatedFeature: GeoJSON.Feature = {
                type: 'Feature',
                geometry: {
                    type: MapTypes.polygon,
                    coordinates:
                        points && points.length > 0
                            ? [[...points, points[0]]]
                            : [],
                },
                properties: {
                    address: location,
                    coordinates: [selectedPosition?.lng, selectedPosition?.lat],
                    country_iso_code,
                    state_iso_code,
                    google_map_state_iso_code,
                    zoom,
                },
            }
            geoJSONSample.features = [...geoJSONSample.features, updatedFeature]
        }
        onAddLocation(geoJSONSample)
    }

    const onUpdatePolygon = (latLngs: number[][]) => {
        setPoints(latLngs)
    }

    const geoCodeLatAndLng = useCallback(() => {
        if (
            selectedPosition?.lat &&
            selectedPosition?.lng &&
            !latError &&
            !lngError
        ) {
            geoCode({
                location: {
                    lat: selectedPosition.lat,
                    lng: selectedPosition.lng,
                },
            })
        }
    }, [
        selectedPosition?.lat,
        selectedPosition?.lng,
        latError,
        lngError,
        geoCode,
    ])

    const disableAddNewLocationButton =
        !location ||
        !country_iso_code ||
        !(state_iso_code || google_map_state_iso_code) ||
        (selectedMapType === MapTypes.polygon && !points.length) ||
        (selectedMapType === MapTypes.circle &&
            (!selectedPosition?.lat || !selectedPosition?.lng)) ||
        !!latError ||
        !!lngError ||
        disableEdit

    return (
        <Box
            display="flex"
            alignItems="flex-start"
            sx={{ width: '100%', height: '100%' }}
        >
            <Box
                sx={{
                    border: '1px solid #d7d9da',
                    borderRadius: '5px',
                    marginRight: '20px',
                    height: '100%',
                    width: '360px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        marginBottom: '20px',
                        padding: '20px',
                        paddingRight: '16px', // Slightly less padding on right to account for scrollbar
                    }}
                >
                    <Box
                        sx={{
                            marginBottom: '20px',
                            borderBottom: '1px solid #d7d9da',
                            paddingBottom: '20px',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: '600',
                                opacity: '0.8',
                                marginBottom: '10px',
                            }}
                        >
                            {t('locationDetails')}
                        </Typography>
                        <Box>
                            <AutoSuggestPlaces
                                value={location}
                                onPlaceSelected={(place, type) =>
                                    onSearchLocation(place.description, type)
                                }
                                disableEdit={disableEdit}
                            />
                        </Box>
                        <Box sx={{ marginTop: '10px' }}>
                            <TextField
                                id="latitude-input"
                                label={t('latitude')}
                                type="number"
                                size="small"
                                fullWidth
                                disabled={!location || disableEdit}
                                value={selectedPosition?.lat || ''}
                                onChange={(e) => {
                                    const value = e.target.value
                                    const lat =
                                        value === ''
                                            ? undefined
                                            : parseFloat(value)
                                    if (value === '') {
                                        setLatError('')
                                    } else if (
                                        lat !== undefined &&
                                        isValidLatitude(lat)
                                    ) {
                                        setLatError('')
                                    } else {
                                        setLatError(
                                            t('latitudeRangeValidation'),
                                        )
                                    }
                                    setSelectedPosition({
                                        ...selectedPosition,
                                        lat,
                                    })
                                    setCoordsGeocodedFlag(false)
                                    setStateIsoCode('')
                                    setCountryIsoCode('')
                                }}
                                sx={{
                                    marginBottom: '10px',
                                    '& .MuiInputBase-root.Mui-disabled': {
                                        backgroundColor: '#f5f5f5',
                                        opacity: 0.6,
                                    },
                                }}
                                InputProps={{
                                    endAdornment: isGeoCodingLoading ? (
                                        <CircularProgress size={14} />
                                    ) : null,
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        geoCodeLatAndLng()
                                    }
                                }}
                                onBlur={geoCodeLatAndLng}
                                error={!!latError}
                            />
                            {latError && (
                                <FormHelperText
                                    error
                                    sx={{ marginBottom: '10px' }}
                                >
                                    {latError}
                                </FormHelperText>
                            )}
                            <TextField
                                id="longitude-input"
                                label={t('longitude')}
                                type="number"
                                size="small"
                                fullWidth
                                disabled={!location || disableEdit}
                                value={selectedPosition?.lng ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value
                                    const lng =
                                        value === ''
                                            ? undefined
                                            : parseFloat(value)
                                    if (value === '') {
                                        setLngError('')
                                    } else if (
                                        lng !== undefined &&
                                        isValidLongitude(lng)
                                    ) {
                                        setLngError('')
                                    } else {
                                        setLngError(
                                            t('longitudeRangeValidation'),
                                        )
                                    }
                                    setSelectedPosition({
                                        ...selectedPosition,
                                        lng,
                                    })
                                    setCoordsGeocodedFlag(false)
                                    setStateIsoCode('')
                                    setCountryIsoCode('')
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        geoCodeLatAndLng()
                                    }
                                }}
                                onBlur={geoCodeLatAndLng}
                                sx={{
                                    '& .MuiInputBase-root.Mui-disabled': {
                                        backgroundColor: '#f5f5f5',
                                        opacity: 0.6,
                                    },
                                }}
                                InputProps={{
                                    endAdornment: isGeoCodingLoading ? (
                                        <CircularProgress size={14} />
                                    ) : null,
                                }}
                                error={!!lngError}
                            />
                            {lngError && (
                                <FormHelperText error>
                                    {lngError}
                                </FormHelperText>
                            )}
                            {!lngError &&
                                !latError &&
                                selectedPosition?.lat &&
                                selectedPosition?.lng &&
                                !coordsGeocodedFlag && (
                                    <FormHelperText
                                        sx={{
                                            color: '#1976d2',
                                            fontSize: '12px',
                                            marginTop: '4px',
                                        }}
                                    >
                                        {t('pressEnterToUseTheseCoordinates')}
                                    </FormHelperText>
                                )}
                        </Box>
                        <Box
                            sx={{
                                marginTop: '10px',
                                marginBottom: '10px',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    opacity: '0.8',
                                    marginRight: '4px',
                                }}
                            >
                                {t('countryCode')}
                            </Typography>
                            <Typography>
                                {location ? (
                                    isGeoCodingLoading ? (
                                        <CircularProgress size={14} />
                                    ) : (
                                        country_iso_code
                                    )
                                ) : (
                                    ''
                                )}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '10px',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    opacity: '0.8',
                                    marginRight: '4px',
                                }}
                            >
                                {t('stateCode')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {isGeoCodingLoading ? (
                                    <CircularProgress size={14} />
                                ) : isLoadingStateList ? (
                                    <CircularProgress size={14} />
                                ) : !ovverideStateCode ? (
                                    <Typography>{state_iso_code}</Typography>
                                ) : (
                                    <Select
                                        id="state-code-select"
                                        value={state_iso_code}
                                        onChange={(e: SelectChangeEvent) =>
                                            setStateIsoCode(e.target.value)
                                        }
                                        displayEmpty
                                        size="small"
                                        disabled={disableEdit}
                                        sx={{
                                            width: '200px',
                                            backgroundColor: 'white',
                                            '& .MuiSelect-select': {
                                                padding: '8px',
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    width: '200px', // Match Select width
                                                    maxHeight: '300px',
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem
                                            value=""
                                            disabled
                                            id="select-state-code-item"
                                        >
                                            <TruncatedText>
                                                {t('selectStateCode')}
                                            </TruncatedText>
                                        </MenuItem>
                                        {stateList.map((state, index) => (
                                            <MenuItem
                                                id={`state-code-select-option-${index}`}
                                                key={state.stateCode}
                                                value={state.stateCode}
                                            >
                                                <TruncatedText>
                                                    {state.name} (
                                                    {state.stateCode})
                                                </TruncatedText>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Box>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: '600',
                                opacity: '0.8',
                                marginBottom: '10px',
                            }}
                        >
                            {t('mapType')}
                        </Typography>
                        <Box>
                            <RadioInput
                                label=""
                                id="map-type-radio"
                                value={selectedMapType}
                                onChange={onSelectMapType}
                                list={maptypeList}
                                disableEdit={disableEdit}
                            />
                        </Box>
                        <Box
                            sx={{
                                marginTop: '20px',
                            }}
                        >
                            {selectedMapType === MapTypes.circle ? (
                                <RangeInput
                                    label={t('chooseRadius')}
                                    id="map-range-bar"
                                    value={range}
                                    onChange={onChangeRange}
                                    disableEdit={disableEdit}
                                />
                            ) : points.length ? (
                                <Box>
                                    <Box
                                        sx={{
                                            background: '#fff',
                                            zIndex: '1',
                                            width: '200px',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                marginRight: '20px',
                                                fontSize: '13px',
                                                opacity: '0.8',
                                                background: '#fff',
                                            }}
                                        >
                                            {t('pointsDetails')}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ paddingTop: '20px' }}>
                                        {points?.map(
                                            (item: any, index: number) => (
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    sx={{
                                                        marginBottom: '10px',
                                                    }}
                                                    key={index}
                                                >
                                                    <Typography
                                                        sx={{
                                                            marginRight: '10px',
                                                            fontSize: '13px',
                                                            opacity: '0.7',
                                                        }}
                                                    >
                                                        P-{index + 1}
                                                    </Typography>

                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                marginRight:
                                                                    '10px',
                                                                fontSize:
                                                                    '14px',
                                                                opacity: '0.9',
                                                            }}
                                                        >
                                                            {item[1]},
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                marginRight:
                                                                    '10px',
                                                                fontSize:
                                                                    '14px',
                                                                opacity: '0.9',
                                                            }}
                                                        >
                                                            {item[0]}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ),
                                        )}
                                    </Box>
                                </Box>
                            ) : (
                                <></>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: '100%',
                        flexShrink: 0,
                        padding: '0 20px 20px 20px',
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={AddLocationDetails}
                        sx={{ width: '100%' }}
                        disabled={disableAddNewLocationButton}
                        id="add-location-button"
                    >
                        {t('addLocation')}
                    </Button>
                </Box>
            </Box>
            <Box sx={{ width: '100%', height: '100%' }}>
                {selectedMapType === MapTypes.circle ? (
                    <MapContainer
                        markerPosition={selectedPosition}
                        radius={range}
                        zoom={zoom}
                        setZoom={setZoom}
                        editable={!disableEdit}
                        updatedSetMarkerPosition={(position: any) =>
                            setSelectedPosition(position)
                        }
                    />
                ) : (
                    <MapContainerPolygon
                        markerPosition={selectedPosition}
                        onPolygonDrawn={(latLngs: number[][]) =>
                            onUpdatePolygon(latLngs)
                        }
                        onPolygonEdited={(latLngs: number[][]) =>
                            onUpdatePolygon(latLngs)
                        }
                        polygon={points}
                        zoom={zoom}
                        setZoom={setZoom}
                        editable={!disableEdit}
                    />
                )}
            </Box>
        </Box>
    )
}

export default MapSpace
